import {
  Close,
  ExpandLess,
  ExpandMore,
  Person,
  Timeline,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { formatDate, getChileUtcOffset } from 'lib/functions/utils';
import { Evaluation } from 'lib/types/models';
import React, { useState } from 'react';

import { StatusChip } from './atoms';
import NodesSection from './NodesSection';
import { NodeResult } from './types';

const chileOffsetInMs = getChileUtcOffset() * 60 * 60 * 1000;

interface EvaluationListDialogProps {
  open: boolean;
  onClose: () => void;
  evaluations: Evaluation[];
}

const EvaluationListDialog: React.FC<EvaluationListDialogProps> = ({
  open,
  onClose,
  evaluations,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (evaluations.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '80vh', width: '700px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              p: 0.75,
              borderRadius: '50%',
            }}
          >
            <Timeline sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Historial de Evaluaciones
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {evaluations[0].label}
              </Typography>
              <Chip
                label={`${evaluations.length} evaluaciones`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {evaluations.map((evaluation, index) => (
          <Card
            key={index}
            sx={{
              border: '1px solid #e0e0e0',
              mb: 2,
              boxShadow: 'none',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#fafafa' },
            }}
            onClick={() =>
              setExpandedIndex(expandedIndex === index ? null : index)
            }
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.7rem', color: 'secondary.main' }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                    >
                      {evaluation.reviewerName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {formatDate(
                        new Date(evaluation.createdAt + chileOffsetInMs),
                        'large',
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip
                    label={evaluation.statusNode.name}
                    severity={evaluation.statusNode.severity}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                    }}
                  >
                    {expandedIndex === index ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </Box>
              </Box>

              <Collapse in={expandedIndex === index}>
                <Divider sx={{ my: 1.5 }} />
                {evaluation.result && evaluation.result.length > 0 && (
                  <NodesSection
                    nodes={evaluation.result as NodeResult[]}
                    hideTitle={false}
                  />
                )}
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default EvaluationListDialog;
